import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Package, Save } from 'lucide-react';
import { fetchPackageDetails, updatePackageAdmin } from '@/api/Admin/userPackage';
import { PlanFormData } from './types';

const initialEditFormData: PlanFormData = {
    name: '',
    description: '',
    price: 0,
    total_slots: 0,
    monthly_commission_rate: 0,
    commission_rate: 0,
    commission_start_month: 1,
    duration_months: 0,
    min_profit_rate: 0,
    is_active: true,
    part_per_user: 0,
    high_profit_rate: 0,
    high_rate_duration: 0,
    tax_rate: 0,
    image: null,
    image_preview: null,
    phases: [],
    benefits: [],
};

export default function PlanEditPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { Id } = useParams<{ Id: string }>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<PlanFormData>(initialEditFormData);

    const pkgId = useMemo(() => {
        const parsed = Number(Id);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }, [Id]);

    useEffect(() => {
        if (!pkgId) {
            message.warning(t('plan.pageDetail.messages.invalidPackageId'));
            navigate('/admin/plans');
            return;
        }

        const loadPackage = async () => {
            setLoading(true);
            try {
                const data = await fetchPackageDetails(pkgId);
                setFormData({
                    name: data.name || '',
                    description: data.description || '',
                    price: parseFloat(data.price || '0'),
                    total_slots: data.total_slots || 0,
                    monthly_commission_rate: parseFloat(data.monthly_commission_rate || '0'),
                    commission_rate: parseFloat(data.commission_rate || '0'),
                    commission_start_month: data.commission_start_month || 1,
                    duration_months: data.duration_months || 0,
                    min_profit_rate: parseFloat(data.min_profit_rate || '0'),
                    is_active: data.is_active,
                    part_per_user: data.part_per_user || 0,
                    high_profit_rate: parseFloat(data.high_profit_rate || '0'),
                    high_rate_duration: data.high_rate_duration || 0,
                    tax_rate: parseFloat(data.tax_rate || '0'),
                    image: null,
                    image_preview: data.image || null,
                    phases: [],
                    benefits: [],
                });
            } catch {
                message.error(t('plan.pageDetail.messages.loadPackageError'));
                navigate('/admin/plans');
            } finally {
                setLoading(false);
            }
        };

        loadPackage();
    }, [pkgId, navigate, t]);

    const handleInputChange = (field: keyof PlanFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (!pkgId) {
            message.warning(t('plan.pageDetail.messages.invalidPackageId'));
            return;
        }

        if (!formData.name.trim()) {
            message.warning(t('plan.nameRequired'));
            return;
        }

        setSaving(true);
        try {
            await updatePackageAdmin(pkgId, {
                name: formData.name,
                description: formData.description,
                price: formData.price,
                total_slots: formData.total_slots,
                monthly_commission_rate: formData.monthly_commission_rate,
                commission_rate: formData.commission_rate,
                commission_start_month: formData.commission_start_month,
                duration_months: formData.duration_months,
                min_profit_rate: formData.min_profit_rate,
                is_active: formData.is_active,
                part_per_user: formData.part_per_user,
                high_profit_rate: formData.high_profit_rate,
                high_rate_duration: formData.high_rate_duration,
                tax_rate: formData.tax_rate,
            });
            message.success(t('plan.updateSuccess'));
            navigate(`/admin/plan/${pkgId}`);
        } catch {
            message.error(t('plan.updateError', 'Cập nhật gói thất bại'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="font-sans">
            <Helmet>
                <title>{t('plan.form.editTitle')}</title>
            </Helmet>

            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-5"
            >
                <ArrowLeft className="w-4 h-4" />
                {t('plan.pageDetail.backToList')}
            </button>

            <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
                    <Package className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('plan.form.editTitle')}</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('plan.pageDetail.subtitle')}</p>
                </div>
            </div>

            {loading ? (
                <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5 text-sm text-gray-500 dark:text-gray-400">
                    {t('plan.pageDetail.loadingData')}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                        <div className="lg:col-span-2 space-y-4">
                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('plan.detail.mainInfo')}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('plan.form.name')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.description')}</label>
                                        <textarea
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.status')}</label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.is_active ? 'true' : 'false'}
                                            onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                                        >
                                            <option value="true">{t('plan.statusActive')}</option>
                                            <option value="false">{t('plan.statusInactive')}</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.price')}</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.price || ''}
                                            onChange={(e) => handleInputChange('price', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.profitSettings')}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.minProfitRate')}</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.min_profit_rate || ''}
                                            onChange={(e) => handleInputChange('min_profit_rate', Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.highProfitRate')}</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.high_profit_rate || ''}
                                            onChange={(e) => handleInputChange('high_profit_rate', Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.highRateDuration')}</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.high_rate_duration || ''}
                                            onChange={(e) => handleInputChange('high_rate_duration', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.durationCommission')}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.durationMonths')}</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.duration_months || ''}
                                            onChange={(e) => handleInputChange('duration_months', Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.commissionF1')}</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.commission_rate}
                                            onChange={(e) => handleInputChange('commission_rate', Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.commissionStartMonth')}</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.commission_start_month}
                                            onChange={(e) => handleInputChange('commission_start_month', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('plan.form.slotTax')}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.totalSlots')}</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.total_slots}
                                            onChange={(e) => handleInputChange('total_slots', Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.partPerUser')}</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.part_per_user}
                                            onChange={(e) => handleInputChange('part_per_user', Number(e.target.value))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('plan.form.taxRate')}</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            value={formData.tax_rate}
                                            onChange={(e) => handleInputChange('tax_rate', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('plan.pageDetail.quickSummary.title')}</h3>
                                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                    <div className="flex items-center justify-between gap-3">
                                        <span>{t('plan.form.name')}</span>
                                        <span className="font-medium text-gray-900 dark:text-white text-right line-clamp-1">{formData.name || '—'}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <span>{t('plan.form.price')}</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{formData.price || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <span>{t('plan.form.totalSlots')}</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{formData.total_slots}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <span>{t('plan.form.status')}</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{formData.is_active ? t('plan.statusActive') : t('plan.statusInactive')}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md font-medium text-sm transition-colors"
                                >
                                    <Save className="h-4 w-4" />
                                    {saving ? t('common.loading', 'Loading...') : t('plan.form.save', 'Lưu thay đổi')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/admin/plan/${pkgId}`)}
                                    className="w-full mt-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
                                >
                                    {t('common.cancel', 'Hủy')}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
